'use client';

import { useRef, useState, type KeyboardEvent } from 'react';
import { Chip } from './chip';
import { Input } from './input';
import styles from './chip-input.module.css';
import { useT } from '@/lib/locale-context';

/** One entry in the list: the value, plus its optional meaning (the per-value
 *  ontology artifact - `AuthoredFieldValue` in domain/view-fields is this shape). */
export interface ChipInputItem {
  value: string;
  description?: string;
}

/**
 * ChipInput - edit a list of discrete values as removable chips with an
 * inline add. The one control for value lists: a comma-joined text input
 * cannot express per-value identity (and silently forbids commas in a
 * value), so lists of tokens use this instead (docs/DESIGN_SYSTEM.md,
 * "Which control for which data").
 *
 *   - **Enter** adds what's typed (so any character, commas included, is a
 *     legal value); **Backspace** on an empty entry removes the last chip;
 *     blur commits pending text so a half-typed value survives a Save click.
 *   - Duplicates are ignored, matching the domain's uniqueness rule.
 *   - `withDescriptions`: each chip opens (click its label) into an inline
 *     editor for that value's own description - the ontology artifact per
 *     value, not only per field. A described value is dot-underlined and
 *     shows its meaning on hover.
 *
 * Sits in a form row on the shared control metrics (`size` sm/md), wrapping
 * to more rows as chips accumulate.
 */
export function ChipInput({
  items,
  onChange,
  size = 'md',
  placeholder,
  ariaLabel,
  withDescriptions = false,
  describePlaceholder,
  disabled,
  invalid,
  testId,
}: {
  items: readonly ChipInputItem[];
  onChange: (items: ChipInputItem[]) => void;
  size?: 'sm' | 'md';
  placeholder?: string;
  /** Names the whole control for screen readers (also labels the add entry). */
  ariaLabel: string;
  /** Chips open into a per-value description editor. */
  withDescriptions?: boolean;
  describePlaceholder?: (value: string) => string;
  disabled?: boolean;
  invalid?: boolean;
  testId?: string;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const entryRef = useRef<HTMLInputElement>(null);

  function commitDraft() {
    const value = draft.trim();
    if (!value) return;
    setDraft('');
    if (items.some((it) => it.value === value)) return;
    onChange([...items, { value }]);
  }

  function removeAt(i: number) {
    onChange(items.filter((_, j) => j !== i));
    setOpenIndex((open) => (open === null || open === i ? null : open > i ? open - 1 : open));
  }

  // Verbatim while typing (the consumer trims at save); emptied out, the key drops.
  function describeAt(i: number, description: string) {
    onChange(
      items.map((it, j) =>
        j !== i ? it : description ? { ...it, description } : { value: it.value },
      ),
    );
  }

  function onEntryKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && draft === '' && items.length > 0) {
      e.preventDefault();
      removeAt(items.length - 1);
    } else if (e.key === 'Escape' && draft !== '') {
      e.preventDefault();
      setDraft('');
    }
  }

  const open = openIndex !== null ? items[openIndex] : undefined;

  return (
    <div className={styles.wrap} data-testid={testId}>
      {/* The box is a click-anywhere target for the entry, like an input's own chrome. */}
      <div
        className={styles.box}
        role="group"
        aria-label={ariaLabel}
        data-size={size}
        data-disabled={disabled || undefined}
        data-invalid={invalid || undefined}
        onClick={(e) => {
          if (e.target === e.currentTarget) entryRef.current?.focus();
        }}
      >
        {items.map((it, i) => {
          // The kit turns off the X (and the label's button) with the box; the
          // title is the value's meaning, or the hint to add one, and the kit
          // keeps it off the X.
          const value = {
            size: 'md',
            tone: 'dim',
            disabled,
            onRemove: () => removeAt(i),
            removeLabel: t('kit.chipInput.remove', { value: it.value }),
          } as const;
          const text = (
            <span className={it.description ? styles.described : undefined}>{it.value}</span>
          );
          return withDescriptions ? (
            <Chip
              key={it.value}
              {...value}
              title={it.description ?? t('kit.chipInput.addDescription')}
              onClick={() => setOpenIndex((o) => (o === i ? null : i))}
              expanded={openIndex === i}
            >
              {text}
            </Chip>
          ) : (
            <Chip key={it.value} {...value} title={it.description}>
              {text}
            </Chip>
          );
        })}
        <input
          ref={entryRef}
          type="text"
          className={styles.entry}
          value={draft}
          placeholder={
            items.length === 0 ? (placeholder ?? t('kit.chipInput.placeholder')) : undefined
          }
          aria-label={ariaLabel}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onEntryKeyDown}
          onBlur={commitDraft}
        />
      </div>
      {withDescriptions && openIndex !== null && open && (
        <Input
          size={size}
          value={open.description ?? ''}
          placeholder={
            describePlaceholder
              ? describePlaceholder(open.value)
              : t('kit.chipInput.describe', { value: open.value })
          }
          aria-label={t('kit.chipInput.descriptionFor', { value: open.value })}
          disabled={disabled}
          onChange={(e) => describeAt(openIndex, e.target.value)}
        />
      )}
    </div>
  );
}
